import React, { useEffect, useState } from "react";
import { shallowEqual } from "react-redux";
import Text from "../../../../../../shared-components/css-modules/lexus/atoms/Text";
import Packs from "./Packs";
import Extras from "./Extras";
import SearchBar from "./SearchBar";
import { trackOptionsTabSwitch } from "../../../../../utils/tracking";
import styles from "./PacksAndExtras.module.scss";
import toggle from "../../../../../../shared-components/css-modules/lexus/organisms/Switch.module.scss";
import { CarConfigConfigureSection } from "../../../../../utils/types/SectionTypes";
import SectionWrapper, { type SectionWrapperPropagationProps } from "../../../common/section-wrapper/SectionWrapper";
import { useCarConfigSelector } from "../../../../../redux/store";
import { extrasSelector, packsSelector } from "../../../../../utils/selectorUtils";
import { CarConfigLabel, useCarConfigLabel } from "../../../../../utils/labelUtils";
import SliderWrapper from "../../../common/spin/sliderWrapper/SliderWrapper";

const PacksAndExtras: React.FC<SectionWrapperPropagationProps<CarConfigConfigureSection>> = ({ sectionRefs }) => {
    // The selectors for packs and extras take into account the current searched value as well
    // This currently works as well considering the searchbar is always rendered independently of the hasPacksAndExtras value
    // might this change in the future consider using items.packs and items.extras instead and look at the MainContent.tsx file in buildandbuy as a reference
    const packs = useCarConfigSelector(packsSelector, shallowEqual);
    const extras = useCarConfigSelector(extrasSelector, shallowEqual);
    const enabledCategoryFilters = useCarConfigSelector((state) => state.extraFilter.appliedCategoryFilters);
    const navigationLabel = useCarConfigLabel("carConfigA11yNavExtra");

    const hasPacks = packs.length !== 0;
    const hasExtras = extras.length !== 0;
    const hasPacksAndExtras = hasPacks && hasExtras;
    const [extrasActive, setExtrasActive] = useState(!hasPacks);

    const [carConfigPacksLabel, carConfigExtrasLabel] = useCarConfigLabel(["carConfigPacks", "carConfigExtras"]);

    useEffect(() => {
        // If filters are changed
        setExtrasActive((active) => {
            if (!active && !hasPacks && hasExtras) {
                // Show extras if no packs are available but extras do
                return true;
            } else if (active && hasPacks && !hasExtras) {
                // Show packs if packs are available but extras do not
                return false;
            }

            // If both are available, do nothing
            return active;
        });
    }, [enabledCategoryFilters]);

    return (
        <SectionWrapper
            sectionId={CarConfigConfigureSection.Packs}
            sectionRef={sectionRefs.get(CarConfigConfigureSection.Packs)}
            hasToggle={hasPacks && hasExtras}
        >
            <SliderWrapper sticky />
            <header className={styles.header}>
                {hasPacksAndExtras && (
                    <Text tag="h2" variant="apheleia-h2" spacing="spacing-4">
                        <CarConfigLabel label="carConfigPacksAndExtras" />
                    </Text>
                )}
                <div className={styles.toggles}>
                    <div>
                        {!hasPacksAndExtras ? (
                            <Text tag="h2" variant="apheleia-h2" spacing="spacing-0">
                                <CarConfigLabel
                                    label={
                                        hasPacks
                                            ? "carConfigPacks"
                                            : hasExtras
                                              ? "carConfigExtras"
                                              : "carConfigPacksAndExtras"
                                    }
                                />
                            </Text>
                        ) : (
                            <nav
                                className={toggle.switch}
                                aria-label={navigationLabel}
                                style={{
                                    gridTemplateColumns: `repeat(${hasPacksAndExtras ? "2" : "1"}, 1fr)`,
                                }}
                            >
                                {hasPacks && (
                                    <>
                                        <input
                                            id="toggle-on"
                                            className={`${toggle.toggle} ${toggle.left}`}
                                            name="togglePacksAndExtras"
                                            value="false"
                                            type="radio"
                                            onChange={() => {
                                                trackOptionsTabSwitch("packs");
                                                setExtrasActive((value) => !value);
                                            }}
                                            checked={!extrasActive}
                                        />
                                        <label htmlFor="toggle-on" className={toggle.option}>
                                            {carConfigPacksLabel}
                                        </label>
                                    </>
                                )}
                                {hasExtras && (
                                    <>
                                        <input
                                            id="toggle-off"
                                            className={`${toggle.toggle} ${toggle.right}`}
                                            name="togglePacksAndExtras"
                                            value="true"
                                            type="radio"
                                            onChange={() => {
                                                trackOptionsTabSwitch("extras");
                                                setExtrasActive((value) => !value);
                                            }}
                                            checked={extrasActive}
                                        />
                                        <label htmlFor="toggle-off" className={toggle.option}>
                                            {carConfigExtrasLabel}
                                        </label>
                                    </>
                                )}
                            </nav>
                        )}
                    </div>
                    <SearchBar extrasActive={extrasActive} />
                </div>
            </header>
            <div>
                <div className={`${toggle.panel} ${!extrasActive ? toggle.active : toggle.inactive}`}>
                    <Packs packs={packs} />
                </div>
                <div className={`${toggle.panel} ${extrasActive ? toggle.active : toggle.inactive}`}>
                    <Extras extras={extras} />
                </div>
            </div>
        </SectionWrapper>
    );
};

export default PacksAndExtras;
